import { DateTime } from 'luxon';
import { AppointmentType } from '@prisma/client';
import { ZONE, bdmDeadlinePassed } from '@web/common';
import type { NachversandAppointmentResDto } from '@web/common';

export type NachversandAppointment = NonNullable<
  NachversandAppointmentResDto['appointment']
>;

// Everything the banner needs for one Nachversand, so it never has to know which kind it
// shows. Only the two BDM kinds: Part 2 has its own screen, and naming it here would
// silently give it the BDM1 copy.
export type ActiveNachversand = {
  kind:
    | typeof AppointmentType.NACHVERSAND_BDM1
    | typeof AppointmentType.NACHVERSAND_BDM2;
  bookingLink: string | null;
  appointment: NachversandAppointmentResDto['appointment'];
  settled: boolean;
  sampleDeadline: DateTime | null;
  bookingWindowOpen: boolean;
};

// Show the banner when an appointment is booked or the booking window is open — and hide it
// once the retrieval is settled (dispatched, returned, picked up, voucher) or the participant
// is not eligible for a Nachversand any more. The revoke also suppresses the past-appointment
// hint, which is correct: nobody should be nudged to re-book a call that would be rejected.
export function isNachversandBannerVisible(
  settled: boolean,
  eligible: boolean,
  isBooked: boolean,
  bookingWindowOpen: boolean,
): boolean {
  if (settled || !eligible) {
    return false;
  }
  return isBooked || bookingWindowOpen;
}

// The gap between a call that took place and the shop's "Versand bestätigen" click: the banner
// offers booking again, because a genuine no-show has to be able to re-book. Without a word of
// explanation that reads as "your call did not count", so an extra line says so.
export function isPastAppointmentHintVisible(
  settled: boolean,
  appointment: NachversandAppointment | null,
  isAppointmentCurrent: boolean,
): boolean {
  return !settled && appointment !== null && !isAppointmentCurrent;
}

// Valid until the END OF ITS DAY, not until endTime: after the call the shop still has to
// click "Versand bestätigen", and telling the participant to book again in that gap would be
// wrong. From the following day on the banner offers booking again. The day boundary is
// Europe/Zurich, like every other BDM deadline, so a travelling participant keeps the study's
// day and not their device's.
export function isAppointmentStillCurrent(
  appointment: NachversandAppointment | null,
  now: DateTime = DateTime.now(),
): boolean {
  if (appointment === null) {
    return false;
  }
  const appointmentDay = DateTime.fromJSDate(appointment.startTime, {
    zone: ZONE,
  }).startOf('day');
  return now.setZone(ZONE).startOf('day') <= appointmentDay;
}

// A slot after the deadline cannot be shipped. cal.com cannot prevent the booking today
// (event-type wide limits only, no URL parameter — cal.com issue #21591), so the banner warns
// instead; rejecting it server-side is the actual fix.
export function isAppointmentAfterDeadline(
  appointment: NachversandAppointment | null,
  deadline: DateTime | null,
): boolean {
  if (appointment === null || deadline === null) {
    return false;
  }
  return bdmDeadlinePassed(
    deadline,
    DateTime.fromJSDate(appointment.startTime),
  );
}

// Would the banner render anything for this kind? The same rule as
// isNachversandBannerVisible, minus the participant-wide eligibility: a kind the banner
// shows nothing for must never occupy the single slot and hide the other one. Without a
// deadline there is nothing to name either; for BDM2 an imported outcome supplies one on
// its own, so there the appointment and the booking window carry the check.
export function isNachversandRenderable(
  nachversand: ActiveNachversand,
  now: DateTime = DateTime.now(),
): boolean {
  return (
    !nachversand.settled &&
    nachversand.sampleDeadline !== null &&
    (isAppointmentStillCurrent(nachversand.appointment, now) ||
      nachversand.bookingWindowOpen)
  );
}

// The one Nachversand the banner may show, never two at once. An open booking window wins,
// being the one the participant can still act on; otherwise BDM1 goes first, its deadline
// being day 23 against BDM2's day 58.
export function pickActiveNachversand(
  bdm1: ActiveNachversand,
  bdm2: ActiveNachversand,
  now: DateTime = DateTime.now(),
): ActiveNachversand | null {
  const candidates = [bdm1, bdm2].filter((candidate) =>
    isNachversandRenderable(candidate, now),
  );
  return (
    candidates.find((candidate) => candidate.bookingWindowOpen) ??
    candidates[0] ??
    null
  );
}
